package com.example.borshi.web.auth

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.example.borshi.web.firebase.FirebaseService
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch

class AuthViewModel {
    var signInState by mutableStateOf<SignInState>(SignInState.Idle)
        private set
    var otpState by mutableStateOf<OtpState>(OtpState.Idle)
        private set
    var signUpState by mutableStateOf<SignUpState>(SignUpState.Idle)
        private set

    private val job = SupervisorJob()
    private val coroutineScope = CoroutineScope(job + Dispatchers.Main)

    private val emailRegex = Regex("^[^@\\n]+@[^@\\n]+\\.[^@\\n]+$")

    private fun mapAuthError(e: Throwable): String {
        val code = e.asDynamic().code as? String
        return when (code) {
            "auth/invalid-phone-number" -> "Invalid phone number"
            "auth/too-many-requests" -> "Too many attempts. Try again later"
            "auth/invalid-verification-code" -> "Incorrect OTP"
            "auth/code-expired" -> "OTP expired. Request a new one"
            "auth/missing-verification-id" -> "Request OTP first"
            "auth/popup-blocked" -> "Sign-in popup was blocked by the browser"
            "auth/popup-closed-by-user" -> "Sign-in was cancelled"
            "auth/cancelled-popup-request" -> "Please complete sign-in in the popup"
            "auth/operation-not-allowed" -> "This sign-in method is not enabled"
            else -> e.message ?: "Something went wrong"
        }
    }

    fun signInWithGoogle() {
        if (signInState is SignInState.Loading) return
        coroutineScope.launch {
            signInState = SignInState.Loading
            otpState = OtpState.Idle
            try {
                val userCredential = FirebaseService.signInWithGoogle()
                signInState = SignInState.Success(userCredential.user)
            } catch (e: Throwable) {
                signInState = SignInState.Error(mapAuthError(e))
            }
        }
    }

    fun signInAnonymously() {
        if (signInState is SignInState.Loading) return
        coroutineScope.launch {
            signInState = SignInState.Loading
            otpState = OtpState.Idle
            try {
                val userCredential = FirebaseService.signInAnonymously()
                signInState = SignInState.Success(userCredential.user)
            } catch (e: Throwable) {
                signInState = SignInState.Error(mapAuthError(e))
            }
        }
    }

    fun signIn(email: String, password: String) {
        val sanitizedEmail = email.trim()
        val sanitizedPassword = password.trim()

        if (!emailRegex.matches(sanitizedEmail)) {
            signInState = SignInState.Error("Enter a valid email")
            return
        }
        if (sanitizedPassword.length < 8) {
            signInState = SignInState.Error("Password must be at least 8 characters")
            return
        }

        coroutineScope.launch {
            signInState = SignInState.Loading
            otpState = OtpState.Idle

            try {
                val userCredential = FirebaseService.signIn(sanitizedEmail, sanitizedPassword)
                signInState = SignInState.Success(userCredential.user)
            } catch (e: Throwable) {
                signInState = SignInState.Error(mapAuthError(e))
            }
        }
    }

    fun sendOtp(phoneNumber: String) {
        if (otpState is OtpState.Sending || otpState is OtpState.Verifying) return // prevent double submit
        val sanitizedPhone = phoneNumber.trim()

        if (!PHONE_REGEX.matches(sanitizedPhone)) {
            otpState = OtpState.Error("Enter phone in E.164 format, e.g. +911234567890")
            return
        }

        coroutineScope.launch {
            otpState = OtpState.Sending
            signInState = SignInState.Idle

            try {
                FirebaseService.sendOtp(sanitizedPhone, RECAPTCHA_CONTAINER_ID)
                otpState = OtpState.OtpSent(sanitizedPhone)
            } catch (e: Throwable) {
                otpState = OtpState.Error(mapAuthError(e))
            }
        }
    }

    fun verifyOtp(otp: String) {
        if (otpState is OtpState.Sending || otpState is OtpState.Verifying) return // prevent double submit
        val sanitizedOtp = otp.trim()

        if (sanitizedOtp.length < 6) {
            otpState = OtpState.Error("OTP must be 6 digits")
            return
        }
        if (otpState !is OtpState.OtpSent) {
            otpState = OtpState.Error("Request OTP first")
            return
        }

        coroutineScope.launch {
            otpState = OtpState.Verifying
            signInState = SignInState.Idle

            try {
                val userCredential = FirebaseService.verifyOtp(sanitizedOtp)
                signInState = SignInState.Success(userCredential.user)
                otpState = OtpState.Idle
            } catch (e: Throwable) {
                otpState = OtpState.Error(mapAuthError(e))
            }
        }
    }

    fun signOut() {
        coroutineScope.launch {
            try {
                FirebaseService.signOut()
                signInState = SignInState.Idle
                otpState = OtpState.Idle
                signUpState = SignUpState.Idle
            } catch (e: Throwable) {
                signInState = SignInState.Error(mapAuthError(e))
            }
        }
    }

    fun signUp(
        email: String,
        password: String,
        name: String = "",
        phone: String = "",
        addressLine1: String = "",
        addressLine2: String = "",
        city: String = "",
        state: String = "",
        postalCode: String = ""
    ) {
        if (signUpState is SignUpState.Loading) return

        val sanitizedEmail = email.trim()
        val sanitizedPassword = password.trim()
        val sanitizedName = name.trim()
        val sanitizedPhone = phone.trim()
        val sanitizedLine1 = addressLine1.trim()
        val sanitizedCity = city.trim()
        val sanitizedPostalCode = postalCode.trim()

        if (sanitizedName.isBlank()) {
            signUpState = SignUpState.Error("Full name is required")
            return
        }
        if (sanitizedPhone.length < 10) {
            signUpState = SignUpState.Error("Enter a valid phone number (10 digits)")
            return
        }
        if (sanitizedLine1.isBlank()) {
            signUpState = SignUpState.Error("Address is required")
            return
        }
        if (sanitizedCity.isBlank()) {
            signUpState = SignUpState.Error("City is required")
            return
        }
        if (sanitizedPostalCode.isBlank()) {
            signUpState = SignUpState.Error("Postal code is required")
            return
        }
        if (!emailRegex.matches(sanitizedEmail)) {
            signUpState = SignUpState.Error("Enter a valid email")
            return
        }
        if (sanitizedPassword.length < 8) {
            signUpState = SignUpState.Error("Password must be at least 8 characters")
            return
        }

        signUpState = SignUpState.Loading
        coroutineScope.launch {
            try {
                FirebaseService.signUp(
                    sanitizedEmail,
                    sanitizedPassword,
                    sanitizedName,
                    sanitizedPhone,
                    sanitizedLine1,
                    addressLine2.trim(),
                    sanitizedCity,
                    state.trim(),
                    sanitizedPostalCode
                )
                signUpState = SignUpState.Idle
            } catch (e: Throwable) {
                signUpState = SignUpState.Error(mapAuthError(e))
            }
        }
    }

    fun clear() {
        job.cancel()
    }

    companion object {
        private const val RECAPTCHA_CONTAINER_ID = "recaptcha-container"
        // If you truly want E.164, consider requiring '+'.
        private val PHONE_REGEX = Regex("^\\+[0-9]{10,15}$")
    }
}

sealed class SignInState {
    data object Idle : SignInState()
    data object Loading : SignInState()
    data class Success(val user: dynamic) : SignInState()
    data class Error(val message: String) : SignInState()
}

sealed class OtpState {
    data object Idle : OtpState()
    data object Sending : OtpState()
    data object Verifying : OtpState()
    data class OtpSent(val phoneNumber: String) : OtpState()
    data class Error(val message: String) : OtpState()
}

sealed class SignUpState {
    data object Idle : SignUpState()
    data object Loading : SignUpState()
    data class Error(val message: String) : SignUpState()
}
